import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { useTranslation } from 'react-i18next';
import logoImage from '../assets/acts-full-logo.svg';
import googleIcon from '../assets/google-96.svg';

import { auth, provider, signInWithPopup } from '../services/firebase';

import LanguageSwitcher from '../components/LanguageSwitcher';
import { trpc } from '../lib/trpc';

const signUpSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: 'signUp.errors.firstNameRequired' }),
    lastName: z.string().min(1, { message: 'signUp.errors.lastNameRequired' }),
    email: z.email({ message: 'signUp.errors.invalidEmail' }),
    password: z.string().min(6, { message: 'signUp.errors.passwordMin' }),
    confirmPassword: z.string(),
    atChurch: z.number({ message: 'signUp.errors.churchRequired' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'signUp.errors.passwordMismatch',
    path: ['confirmPassword'],
  });

const mobileSignUpSchema = z.object({
  mobile: z
    .string()
    .min(9, { message: 'signUp.errors.invalidThaiPhone' })
    .refine(
      (val) => {
        const clean = val.replace(/[^\d+]/g, '');
        return /^0[689]\d{8}$/.test(clean) || /^\+66[689]\d{8}$/.test(clean) || /^66[689]\d{8}$/.test(clean);
      },
      { message: 'signUp.errors.invalidThaiPhone' }
    ),
});

type SignUpFormInputs = z.infer<typeof signUpSchema>;
type MobileSignUpInputs = z.infer<typeof mobileSignUpSchema>;

const SignUp = () => {
  const { t } = useTranslation();




  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<SignUpFormInputs>({
    resolver: zodResolver(signUpSchema),
    mode: 'onBlur',
  });

  // Fetch atChurches using TRPC
  const { data: atChurchesData, isLoading: isAtChurchesLoading } =
    trpc.auth.getAtChurches.useQuery({
      structureId: 1, // Root structure ID
    });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'email' | 'google' | 'mobile'>('google');

  const {
    register: registerMobile,
    handleSubmit: handleSubmitMobile,
    formState: { errors: mobileErrors },
  } = useForm<MobileSignUpInputs>({
    resolver: zodResolver(mobileSignUpSchema),
    mode: 'onBlur',
  });

  // TRPC mutation for creating user
  const createUserWithEmailMutation =
    trpc.user.createUserWithEmail.useMutation();
  const createUserWithGoogleMutation =
    trpc.user.createUserWithGoogle.useMutation();
  const createUserWithMobileMutation =
    trpc.user.createUserWithMobile.useMutation();

  const handleMobileSignUp = async (data: MobileSignUpInputs) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await createUserWithMobileMutation.mutateAsync({
        mobile: data.mobile,
      });

      if (result.success) {
        // Redirect to verification page with phone and ref parameter
        const refParam = result.ref ? `&ref=${encodeURIComponent(result.ref)}` : '';
        const otpParam = (result as any).code ? `&otp=${encodeURIComponent((result as any).code)}` : '';
        window.location.href = `/verification?phone=${encodeURIComponent(result.phone)}&type=mobile${refParam}${otpParam}`;
      }
    } catch (err: any) {
      console.error('Mobile registration error:', err);
      if (err.data?.code === 'NOT_FOUND') {
        setError(t('signUp.errors.mobileNotMember'));
      } else if (err.data?.code === 'CONFLICT') {
        setError(t('signUp.errors.memberAlreadyRegistered'));
      } else if (err.data?.code === 'TOO_MANY_REQUESTS') {
        setError(t('verification.errors.rateLimited'));
      } else if (err.data?.code === 'BAD_REQUEST') {
        setError(t('signUp.errors.invalidThaiPhone'));
      } else {
        setError(t(err.message) || t('signUp.errors.registrationFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async (data: SignUpFormInputs) => {
    // Validate required fields for Google sign-up
    if (!data.firstName?.trim() || !data.lastName?.trim()) {
      setError(
        t('signUp.errors.nameRequired') ||
        'First name and last name are required'
      );
      return;
    }

    if (!data.atChurch) {
      setError(t('signUp.errors.churchRequired') || 'Please select a church');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('Google Sign-Up initiated with data:', data);

      // Add the 'prompt' parameter to force account selection
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      console.log('Opening Google sign-in popup...');
      const userCredential = await signInWithPopup(auth, provider);
      console.log('Google sign-in successful:', userCredential.user.email);

      createUserWithGoogleMutation.mutate(
        {
          email: userCredential.user.email!,
          password: '', // Google sign-up doesn't require a password
          first_name: data.firstName,
          last_name: data.lastName,
          ChurchID: data.atChurch,
        },
        {
          onSuccess: (res) => {
            if (res) {
              console.log('User created successfully:', res);
              // Redirect to verification page with the Google email
              // window.location.href = `/verification?email=${encodeURIComponent(userCredential.user.email!)}`;
              window.location.href = '/confirmation';
            }
          },
          onError: (error) => {
            console.error('Error creating user:', error);
            if (error.data?.code === 'CONFLICT') {
              setError(t('signUp.errors.emailInUse'));
            }
            else {
              setError(t('signUp.errors.emailRegistrationFailed'));
            }
          },
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Google registration error:', error);
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        setError(
          t('signUp.errors.popupClosedError') ||
          'Sign-up cancelled. Please try again.'
        );
      } else if (error.code === 'auth/popup-blocked') {
        setError(
          t('signUp.errors.popupBlockedError') ||
          'Pop-up was blocked. Please allow pop-ups and try again.'
        );
      } else {
        setError(
          t('signUp.errors.googleRegistrationFailed') ||
          `Error: ${error.message}`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (data: SignUpFormInputs) => {
    setIsLoading(true);
    setError('');

    try {
      createUserWithEmailMutation.mutate(
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          password: data.password,
          ChurchID: data.atChurch,
        },
        {
          onSuccess: (res) => {
            if (res) {
              console.log('User created successfully:', res);
              // Redirect to verification page after successful signup, include email so verification page knows
              // window.location.href = `/verification?email=${encodeURIComponent(data.email)}`;
              window.location.href = '/confirmation';
            }
          },
          onError: (error) => {
            console.error('Error creating user:', error);
            if (error.data?.code === 'CONFLICT') {
              setError(t('signUp.errors.emailInUse'));
            } else {
              setError(t('signUp.errors.emailRegistrationFailed'));
            }
          },
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Email registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError(t('signUp.errors.emailInUse'));
      } else {
        setError(t('signUp.errors.emailRegistrationFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`md:!bg-blue-200`}>
      <div
        className={`flex h-screen flex-col items-center justify-center overflow-auto`}
      >
        <div className='w-full max-w-md bg-white p-6 md:shadow-md md:rounded-lg'>
          <img
            src={logoImage}
            alt='Acts Camp Logo'
            className='w-full h-35 md:h-40'
          />
          <div className='flex justify-between items-center align-middle pt-2 pb-4'>
            <div />
            <h2 className='text-2xl font-bold text-center'>
              {t('signUp.title')}
            </h2>
            <div className='flex justify-end'>
              <LanguageSwitcher />
            </div>
          </div>
          {/* Registration Method Tabs */}
          <div className='flex border-b border-gray-200 mb-6'>
            <button
              className={`py-2 px-3 sm:px-4 font-medium text-sm ${activeTab === 'google' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('google');
                setError('');
              }}
            >
              {t('signUp.tabs.google')}
            </button>
            <button
              className={`py-2 px-3 sm:px-4 font-medium text-sm ${activeTab === 'email' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('email');
                setError('');
              }}
            >
              {t('signUp.tabs.email')}
            </button>
            <button
              className={`py-2 px-3 sm:px-4 font-medium text-sm ${activeTab === 'mobile' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('mobile');
                setError('');
              }}
            >
              {t('signUp.tabs.mobile')}
            </button>
          </div>
          {error && (
            <div className='mb-4 text-sm text-red-600 text-center'>{error}</div>
          )}
          {/* Email/Password Registration Form */}
          {activeTab === 'email' && (
            <form
              onSubmit={handleSubmit(handleEmailSignUp)}
              className='space-y-4'
            >
              <div>
                <label
                  htmlFor='churchId'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.church')}
                </label>
                <select
                  id='atChurch'
                  {...register('atChurch', {
                    setValueAs: (value) =>
                      value === '' ? undefined : parseInt(value, 10),
                  })}
                  disabled={isAtChurchesLoading}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.atChurch
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                    }`}
                >
                  {isAtChurchesLoading ? (
                    <option>{t('signUp.fields.loadingChurches')}</option>
                  ) : (
                    <>
                      <option value=''>
                        {t('signUp.fields.selectChurch')}
                      </option>
                      {atChurchesData?.map((church) => (
                        <option key={church.StructID} value={church.StructID}>
                          {church.StrucName}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {errors.atChurch && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(errors.atChurch.message || '')}
                  </p>
                )}
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='firstName'
                    className='block text-sm font-medium text-gray-700'
                  >
                    {t('signUp.fields.firstName')}
                  </label>
                  <input
                    id='firstName'
                    type='text'
                    {...register('firstName')}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.firstName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                      }`}
                    placeholder={t('signUp.placeholders.firstName')}
                  />
                  {errors.firstName && (
                    <p className='mt-1 text-sm text-red-600'>
                      {t(errors.firstName.message || '')}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor='lastName'
                    className='block text-sm font-medium text-gray-700'
                  >
                    {t('signUp.fields.lastName')}
                  </label>
                  <input
                    id='lastName'
                    type='text'
                    {...register('lastName')}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.lastName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                      }`}
                    placeholder={t('signUp.placeholders.lastName')}
                  />
                  {errors.lastName && (
                    <p className='mt-1 text-sm text-red-600'>
                      {t(errors.lastName.message || '')}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.email')}
                </label>
                <input
                  id='email'
                  type='email'
                  {...register('email')}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.email
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                    }`}
                  placeholder={t('signUp.placeholders.email')}
                />
                {errors.email && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(errors.email.message || '')}
                  </p>
                )}
                <p className='mt-1 text-xs text-gray-500'>
                  {t('signUp.messages.churchEmailRequired')}
                </p>
              </div>

              <div>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.password')}
                </label>
                <input
                  id='password'
                  type='password'
                  {...register('password')}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.password
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                    }`}
                  placeholder={t('signUp.placeholders.password')}
                />
                {errors.password && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(errors.password.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor='confirmPassword'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.confirmPassword')}
                </label>
                <input
                  id='confirmPassword'
                  type='password'
                  {...register('confirmPassword')}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.confirmPassword
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                    }`}
                  placeholder={t('signUp.placeholders.password')}
                />
                {errors.confirmPassword && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(errors.confirmPassword.message || '')}
                  </p>
                )}
              </div>

              <button
                type='submit'
                disabled={isLoading}
                className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
              >
                {isLoading
                  ? t('signUp.buttons.creatingAccount')
                  : t('signUp.buttons.signUpEmail')}
              </button>
            </form>
          )}
          {/* Google Registration */}
          {activeTab === 'google' && (
            <div className='space-y-6'>
              <div>
                <label
                  htmlFor='atChurch'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.church')}
                </label>
                <select
                  id='atChurch'
                  {...register('atChurch', {
                    setValueAs: (value) =>
                      value === '' ? undefined : parseInt(value, 10),
                  })}
                  disabled={isAtChurchesLoading}
                  className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.atChurch
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-blue-500'
                    }`}
                >
                  {isAtChurchesLoading ? (
                    <option>{t('signUp.fields.loadingChurches')}</option>
                  ) : (
                    <>
                      <option value=''>
                        {t('signUp.fields.selectChurch')}
                      </option>
                      {atChurchesData?.map((church) => (
                        <option key={church.StructID} value={church.StructID}>
                          {church.StrucName}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {errors.atChurch && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(errors.atChurch.message || '')}
                  </p>
                )}
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    htmlFor='googleFirstName'
                    className='block text-sm font-medium text-gray-700'
                  >
                    {t('signUp.fields.firstName')}
                  </label>
                  <input
                    id='googleFirstName'
                    type='text'
                    {...register('firstName')}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.firstName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                      }`}
                    placeholder={t('signUp.placeholders.firstName')}
                  />
                  {errors.firstName && (
                    <p className='mt-1 text-sm text-red-600'>
                      {t(errors.firstName.message || '')}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor='googleLastName'
                    className='block text-sm font-medium text-gray-700'
                  >
                    {t('signUp.fields.lastName')}
                  </label>
                  <input
                    id='googleLastName'
                    type='text'
                    {...register('lastName')}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.lastName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                      }`}
                    placeholder={t('signUp.placeholders.lastName')}
                  />
                  {errors.lastName && (
                    <p className='mt-1 text-sm text-red-600'>
                      {t(errors.lastName.message || '')}
                    </p>
                  )}
                </div>
              </div>
              <button
                type='button'
                onClick={() => {
                  const data = getValues();
                  handleGoogleSignUp(data);
                }}
                disabled={isLoading || isAtChurchesLoading}
                className='flex w-full items-center justify-center gap-3 rounded-md bg-white py-2 px-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 border border-gray-300'
              >
                <img src={googleIcon} alt='Google Icon' className='h-5 w-5' />
                <span>
                  {isLoading
                    ? t('signUp.buttons.creatingAccount')
                    : t('signUp.buttons.signUpGoogle')}
                </span>
              </button>
              <div className='text-xs text-gray-500 text-center'>
                <p>{t('signUp.messages.googleRedirectInfo')}</p>
                <p className='mt-1'>
                  {t('signUp.messages.googleAccountCreationInfo')}
                </p>
              </div>
            </div>
          )}

          {/* Mobile Registration Form */}
          {activeTab === 'mobile' && (
            <form
              onSubmit={handleSubmitMobile(handleMobileSignUp)}
              className='space-y-4'
            >
              <div>
                <label
                  htmlFor='mobilePhone'
                  className='block text-sm font-medium text-gray-700'
                >
                  {t('signUp.fields.mobile')}
                </label>
                <div className='mt-1 relative rounded-md shadow-sm'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <span className='text-gray-500 sm:text-sm font-medium'>🇹🇭 +66</span>
                  </div>
                  <input
                    id='mobilePhone'
                    type='tel'
                    {...registerMobile('mobile')}
                    className={`block w-full pl-16 rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${
                      mobileErrors.mobile
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder='081-234-5678'
                  />
                </div>
                {mobileErrors.mobile && (
                  <p className='mt-1 text-sm text-red-600'>
                    {t(mobileErrors.mobile.message || '')}
                  </p>
                )}
                <p className='mt-1 text-xs text-gray-500'>
                  {t('signUp.messages.thaiPhoneNotice')}
                </p>
              </div>


              <button
                type='submit'
                disabled={isLoading}
                className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
              >
                {isLoading
                  ? t('signUp.buttons.creatingAccount')
                  : t('signUp.buttons.signUpMobile')}
              </button>
            </form>
          )}
          {/* Sign In Option */}

          <div className='mt-6 text-center'>
            <p className='text-sm text-gray-600'>
              {t('signUp.messages.alreadyHaveAccount')}{' '}
              <button
                onClick={() => window.location.replace('/')}
                className='font-medium text-blue-600 hover:text-blue-500'
              >
                {t('signUp.messages.signInLink')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
